import { Clickable } from "@client/components/Clickables";
import React from "react";

import { Header2026BodyBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import ContactUsForm from "../../ContactUsForm";
import { Lockup } from "./Lockup";
import { MENU_ID, Menu } from "./Menu";
import { useTopbarScroll } from "./useTopbarScroll";

import styles from "./index.module.scss";

interface Props {
    value: Header2026BodyBlock;
    /**
     * The intro entrance, driven by the page's intro sequence. `"preload"`
     * parks the bar above the viewport without a transition (render it from
     * the server so the first paint has it there). `"entered"` releases it on
     * the return curve, unless the reader has already scrolled it away.
     * Omitted, the bar is simply present.
     */
    entrance?: "preload" | "entered";
    /** Stories only: render with the phone menu open */
    defaultMenuOpen?: boolean;
}

/**
 * The 2026 topbar: lockup, the header setting's links and "Get connected"
 * (the contact modal), and the phone's menu.
 */
export const Header2026 = (props: Props) => {
    const barRef = React.useRef<HTMLElement>(null);
    const toggleRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);
    const { away, back, solid, onDark } = useTopbarScroll(barRef);
    const [menuOpen, setMenuOpen] = React.useState(!!props.defaultMenuOpen);
    const closeMenu = React.useCallback(() => setMenuOpen(false), []);

    const className = concatClassNames([
        styles.topbar,
        away ? styles.away : "",
        back || (props.entrance === "entered" && !away) ? styles.back : "",
        props.entrance === "preload" ? styles.preload : "",
        solid ? styles.solid : "",
        // The open menu is light: the bar keeps its light ground and dark type
        onDark && !menuOpen ? styles.onDark : "",
    ]);

    return (
        <>
            <header ref={barRef} className={className}>
                <Clickable
                    className={styles.logo}
                    href="/"
                    aria-label="thelab, home"
                >
                    <Lockup />
                </Clickable>
                <nav className={styles.nav}>
                    {props.value.map(({ id, value }) =>
                        // A deleted or unpublished target leaves no URL
                        value.url === null ? null : (
                            <Clickable
                                key={id}
                                className={styles.link}
                                href={value.url}
                            >
                                {value.display_name ?? value.url}
                            </Clickable>
                        ),
                    )}
                    {/* The page's one instance with the contact hooks */}
                    <ContactUsForm>
                        <Clickable className={styles.link}>
                            Get connected
                        </Clickable>
                    </ContactUsForm>
                </nav>
                <Clickable
                    ref={toggleRef}
                    className={styles.toggle}
                    aria-expanded={menuOpen}
                    aria-controls={MENU_ID}
                    onPress={() => setMenuOpen(!menuOpen)}
                >
                    {menuOpen ? "Close" : "Menu"}
                </Clickable>
            </header>
            <Menu
                links={props.value}
                isOpen={menuOpen}
                onClose={closeMenu}
                toggleRef={toggleRef}
            />
        </>
    );
};
